import { useEffect, useState } from 'react'
import {
  useParams,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom'
import { useCourse } from '../../hooks/useApi'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useAppStore } from '@/store/useAppStore'
import { ROUTES, WEDDING_COURSE_ID, WEDDING_BOT_PAYMENT_URL } from '@shared/config/routes'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import {
  CoursePaymentModal,
  useBuyCourseMutation,
  useDoubleSubmitGuard,
  usePaymentMethodsQuery,
  useStartPayment,
  type CoursePaymentSubmit,
} from '@features/payments'
import { FileText, Lock } from 'lucide-react'
import { Icon } from '@iconify/react'
import type { ModuleInfo, LessonWithProgress } from '../../types/api'
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen'
import CourseLanding from './CourseLanding'
import './CourseDetailPage.css'

interface CourseDetailPageProps {
  /** Если задан — берём этот id вместо :courseId из URL (для алиасов вроде /wc). */
  courseId?: number
  /** Базовый путь для возврата с auth (для /wc — '/wc'). По умолчанию — текущий URL. */
  buyRedirectPath?: string
}

const CourseDetailPage = ({
  courseId: courseIdProp,
  buyRedirectPath,
}: CourseDetailPageProps = {}) => {
  const params = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useAppStore((s) => s.isAuthenticated)

  const courseIdNumber =
    courseIdProp ?? (params.courseId ? parseInt(params.courseId, 10) : null)

  // Для гостя бэк отдаёт 401 — useCourse запускаем только для авторизованных.
  const { data, isLoading, error } = useCourse(
    isAuthenticated ? courseIdNumber : null
  )

  const [isPaymentOpen, setPaymentOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const buyMutation = useBuyCourseMutation()
  const guard = useDoubleSubmitGuard()
  const startPayment = useStartPayment()
  const platform = usePlatform()
  const { data: paymentMethodsData } = usePaymentMethodsQuery(isAuthenticated)
  const paymentMethods = paymentMethodsData?.methods ?? []

  useTelegramBackButton(true)

  // Возврат с auth с ?buy=1 — авто-открытие модалки оплаты и чистка query.
  // Для свадебного курса флоу другой (бот), модалка не нужна — просто чистим параметр.
  useEffect(() => {
    const search = new URLSearchParams(location.search)
    if (search.get('buy') !== '1') return
    if (!isAuthenticated) return
    if (courseIdNumber !== WEDDING_COURSE_ID) {
      setPaymentOpen(true)
    }
    search.delete('buy')
    const next = search.toString()
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '' },
      { replace: true }
    )
  }, [isAuthenticated, courseIdNumber, location.pathname, location.search, navigate])

  useEffect(() => {
    const rows = Array.from(
      document.querySelectorAll<HTMLElement>('.module-lessons-scroll'),
    )
    const onWheel = (e: WheelEvent) => {
      const el = e.currentTarget as HTMLElement
      if (el.scrollWidth <= el.clientWidth) return
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      const atStart = el.scrollLeft <= 0
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return
      el.scrollLeft += e.deltaY
      e.preventDefault()
    }
    rows.forEach((el) =>
      el.addEventListener('wheel', onWheel, { passive: false }),
    )
    return () =>
      rows.forEach((el) => el.removeEventListener('wheel', onWheel))
  }, [data])

  const handleLessonClick = (lessonId: number, isLocked: boolean) => {
    if (isLocked) return
    navigate(`/courses/${courseIdNumber}/lesson/${lessonId}`)
  }

  const handleBuyClick = () => {
    // Свадебный курс оплачивается через отдельного Telegram-бота, минуя
    // web-флоу (Робокассу). Auth не требуется — оплата вне нашей системы.
    if (courseIdNumber === WEDDING_COURSE_ID) {
      platform.openLink(WEDDING_BOT_PAYMENT_URL)
      return
    }
    if (!isAuthenticated) {
      const redirectPath = buyRedirectPath ?? location.pathname
      const target = `${redirectPath}?buy=1`
      navigate(`${ROUTES.AUTH}?redirect=${encodeURIComponent(target)}`)
      return
    }
    setSubmitError(null)
    setPaymentOpen(true)
  }

  const closePaymentModal = () => {
    if (buyMutation.isLoading) return
    setPaymentOpen(false)
    setSubmitError(null)
  }

  const handlePaymentSubmit = ({
    saveCard,
    paymentMethodId,
  }: CoursePaymentSubmit) => {
    if (courseIdNumber == null) return
    // Замок берём до всего остального: по isLoading второй тап проскакивал и
    // создавал второй счёт.
    if (!guard.tryStart()) return
    setSubmitError(null)
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      {
        course_id: courseIdNumber,
        provider: 'robokassa',
        save_card: saveCard,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: (invoice) => {
          startPayment(invoice, tab)
          setPaymentOpen(false)
        },
        onError: (err) => {
          tab.abort()
          const status = (err as { response?: { status?: number } }).response
            ?.status
          if (status === 409) {
            setSubmitError('У вас уже есть незавершённый платёж по этому курсу.')
          } else if (status === 502) {
            setSubmitError(
              'Не удалось связаться с провайдером. Попробуйте ещё раз.'
            )
          } else {
            setSubmitError('Не удалось создать платёж. Попробуйте ещё раз.')
          }
        },
        onSettled: (_invoice, error) => guard.finish(!error),
      }
    )
  }

  if (courseIdNumber == null || isNaN(courseIdNumber)) {
    return <Navigate to="/courses" replace />
  }

  // Гость: данных курса с бэка нет (требует auth) — показываем только лендинг.
  // Клик «Купить» → редирект на /auth, после возврата модалка откроется уже
  // в авторизованной ветке ниже по флагу ?buy=1.
  if (!isAuthenticated) {
    return (
      <div className="course-detail-page safe-area-inset">
        <CourseLanding onBuyClick={handleBuyClick} />
      </div>
    )
  }

  if (isLoading) {
    return <LoadingScreen isLoading={true} />
  }

  if (error) {
    const errorDetail = (error as any)?.response?.data?.detail
    const isAccessDenied = (error as any)?.response?.status === 403

    if (isAccessDenied) {
      return (
        <div className="course-detail-error">
          <h2>Нет доступа</h2>
          <p>{errorDetail || 'У вас нет доступа к этому курсу'}</p>
          <button onClick={() => navigate('/courses')}>К курсам</button>
        </div>
      )
    }

    return (
      <div className="course-detail-error">
        <h2>Ошибка загрузки</h2>
        <p>Не удалось загрузить курс</p>
        <button onClick={() => navigate('/courses')}>К курсам</button>
      </div>
    )
  }

  if (!data?.course) {
    return (
      <div className="course-detail-error">
        <h2>Курс не найден</h2>
        <button onClick={() => navigate('/courses')}>К курсам</button>
      </div>
    )
  }

  const { course } = data
  const hasAccess = course.access
  const hasModules = course.modules && course.modules.length > 0
  const landing = course.landing
  // Детальный ответ не содержит trial_lesson_count в base — считаем по урокам.
  const hasTrial =
    course.modules?.some((m: ModuleInfo) =>
      m.lessons?.some((l: LessonWithProgress) => l.base.is_trial)
    ) ?? false
  // Полноэкранный лендинг — только если совсем нет демо-уроков.
  // Если есть триалы — даём провалиться в экран курса с замками на платных уроках.
  const showFullLanding = !hasAccess && !!landing && !hasTrial
  const showDemoBadge = !hasAccess && hasTrial

  const handleLockedLessonClick = () => {
    navigate(ROUTES.WC)
  }

  // Подсчёт общего прогресса
  const totalLessons =
    course.modules?.reduce(
      (acc: number, m: ModuleInfo) => acc + (m.base.lesson_count || 0),
      0
    ) || 0
  const viewedLessons =
    course.modules?.reduce(
      (acc: number, m: ModuleInfo) => acc + (m.progress?.views || 0),
      0
    ) || 0
  const progressPercent =
    totalLessons > 0 ? (viewedLessons / totalLessons) * 100 : 0

  // Первое изображение курса
  const courseImage =
    course.base.header_images?.[0] || course.base.preview_icon_url

  return (
    <div className="course-detail-page safe-area-inset">
      {/* Лендинг полноэкранно — только если нет доступа и нет демо-уроков.
          С демо проваливаемся в обычный экран курса и показываем бейдж. */}
      {showFullLanding && <CourseLanding onBuyClick={handleBuyClick} />}

      <div className="course-detail-content">
        {!showFullLanding && (
          <>
            <h1 className="course-detail-title">{course.base.name}</h1>

            {courseImage && (
              <div className="course-detail-image-wrapper">
                <img
                  src={courseImage}
                  alt={course.base.name}
                  className="course-detail-image"
                />
              </div>
            )}

            <div className="course-detail-meta">
              <div className="course-meta-item">
                <Icon icon="hugeicons:task-01" width={24} height={24} />
                <span>{totalLessons} уроков</span>
              </div>
            </div>

            {hasAccess && totalLessons > 0 && (
              <div className="course-detail-progress">
                <div className="course-detail-progress-bar">
                  <div
                    className="course-detail-progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="course-detail-progress-info">
                  <span className="course-detail-progress-label">Пройдено</span>
                  <span className="course-detail-progress-count">
                    {viewedLessons}/{totalLessons}
                  </span>
                </div>
              </div>
            )}

            {course.base.description && (
              <p className="course-detail-description">
                {course.base.description}
              </p>
            )}
          </>
        )}

        {showDemoBadge && (
          <button
            type="button"
            className="course-demo-cta"
            onClick={handleLockedLessonClick}
          >
            Купить полный курс
          </button>
        )}

        {/* Modules: рендерим, если они есть. Замок на уроке вешаем, если у курса
            нет доступа и урок не пробный (либо явно заблокирован модулем/уроком). */}
        {hasModules && (
          <div className="course-modules">
            {course.modules.map((module: ModuleInfo) => {
              const moduleViews = module.progress?.views || 0
              const moduleLessons = module.base.lesson_count || 0
              const isModuleLocked = module.base.is_locked

              return (
                <div key={module.base.module_id} className="module-section">
                  <div className="module-header">
                    <div className="module-header-left">
                      {isModuleLocked && (
                        <Lock size={18} className="module-lock-icon" />
                      )}
                      <h3 className="module-title">{module.base.name}</h3>
                    </div>
                    <span className="module-progress">
                      {moduleViews}/{moduleLessons}
                    </span>
                  </div>

                  {module.lessons && module.lessons.length > 0 && (
                    <div className="module-lessons-scroll">
                      {module.lessons.map(
                        (lesson: LessonWithProgress, lessonIndex: number) => {
                          const isLessonLocked =
                            isModuleLocked ||
                            lesson.base.is_locked ||
                            (!hasAccess && !lesson.base.is_trial)

                          return (
                            <div
                              key={lesson.base.lesson_id}
                              className={`lesson-card ${isLessonLocked ? 'locked' : ''} ${lesson.is_viewed ? 'viewed' : ''}`}
                              onClick={() => {
                                if (isLessonLocked) {
                                  handleLockedLessonClick()
                                  return
                                }
                                handleLessonClick(
                                  lesson.base.lesson_id,
                                  false
                                )
                              }}
                            >
                              <div className="lesson-card-image-wrapper">
                                {lesson.card.icon_url ? (
                                  <img
                                    src={lesson.card.icon_url}
                                    alt=""
                                    className="lesson-card-image"
                                  />
                                ) : (
                                  <div className="lesson-card-placeholder">
                                    <FileText size={24} />
                                  </div>
                                )}
                                {lesson.is_viewed && !isLessonLocked && (
                                  <div className="lesson-status-icon viewed">
                                    <Icon
                                      icon="iconamoon:check-bold"
                                      width={24}
                                      height={24}
                                      color="#FFFFFF"
                                    />
                                  </div>
                                )}
                                {isLessonLocked && (
                                  <div className="lesson-status-icon locked">
                                    <Lock size={14} />
                                  </div>
                                )}
                              </div>
                              <div className="lesson-card-info">
                                <span className="lesson-card-number">
                                  Урок {lessonIndex + 1}
                                </span>
                                <span className="lesson-card-title">
                                  {lesson.card.title}
                                </span>
                              </div>
                            </div>
                          )
                        }
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Нет доступа и нет лендинга */}
        {!hasAccess && !landing && (
          <div className="course-no-access">
            <Icon icon="solar:lock-linear" width={40} height={40} />
            <p>У вас нет доступа к этому курсу</p>
          </div>
        )}
      </div>

      {isPaymentOpen && (
        <CoursePaymentModal
          courseName={course.base.name}
          price={course.base.price}
          paymentMethods={paymentMethods}
          onSubmit={handlePaymentSubmit}
          onClose={closePaymentModal}
          isSubmitting={buyMutation.isLoading}
          errorText={submitError}
        />
      )}
    </div>
  )
}

export default CourseDetailPage
